/**
 * Turning values into the words a person reads.
 *
 * Carried over from the old client, where these were already load-bearing and
 * already correct — a relative time that says "just now" for anything under a
 * minute, and a URL guard that refuses anything that is not http(s).
 */

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const NUMBER = new Intl.NumberFormat();

const UNITS = [
  ["year", 31_536_000],
  ["month", 2_592_000],
  ["week", 604_800],
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
];

/* The server stores and sends naive UTC: "2026-09-23T03:48:01", no zone.
   `new Date` reads a date-time with no zone as local time, so every relative
   time was off by the reader's UTC offset: a file uploaded a second ago said
   "2 hours ago" in Warsaw. A stamp with no zone is UTC; one that carries a
   zone, and a bare date, are left alone. */
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/i;

/** A server timestamp as a real instant. */
export function instant(iso) {
  if (iso instanceof Date) return iso;
  const text = String(iso);
  return new Date(text.includes("T") && !ZONED.test(text) ? `${text}Z` : text);
}

export function relativeTime(iso) {
  if (!iso) return "";
  const then = instant(iso).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = (then - Date.now()) / 1000;
  const magnitude = Math.abs(seconds);
  if (magnitude < 45) return "just now";

  for (const [unit, size] of UNITS) {
    if (magnitude >= size) return RELATIVE.format(Math.round(seconds / size), unit);
  }
  return RELATIVE.format(Math.round(seconds / 60), "minute");
}

export function formatNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? NUMBER.format(number) : "—";
}

export function formatBytes(bytes) {
  const number = Number(bytes);
  if (!Number.isFinite(number) || number <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const power = Math.min(Math.floor(Math.log(number) / Math.log(1024)), units.length - 1);
  const scaled = number / 1024 ** power;
  return `${scaled >= 10 || power === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[power]}`;
}

export function minutesToRead(seconds) {
  return Math.max(1, Math.round((seconds || 0) / 60));
}

/**
 * A URL that is safe to put in an href.
 *
 * Every link in this app comes from a feed somebody else wrote, so a
 * `javascript:` scheme is not hypothetical. Anything that is not http or https
 * becomes empty, which renders as a dead link rather than as a trap.
 */
export function safeUrl(value) {
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

export function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return "—";
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const CLOCK = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

/** The time of day a moment fell at, the way this reader's locale writes it. */
export function clockTime(moment) {
  const date = instant(moment);
  return Number.isNaN(date.getTime()) ? "" : CLOCK.format(date);
}

/** How long until a moment, for an expiry. Past moments read as "expired". */
export function untilTime(iso) {
  if (!iso) return "";
  const remaining = (instant(iso).getTime() - Date.now()) / 1000;
  return remaining <= 0 ? "expired" : formatDuration(remaining);
}

/** The days a removed attachment's file has left in Files, in whole days. */
export function deletedIn(iso) {
  if (!iso) return "";
  const days = Math.ceil((instant(iso).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return "deleted today";
  return days === 1 ? "deleted in 1 day" : `deleted in ${days} days`;
}
